import React, { useState } from 'react';
import { Pressable } from 'react-native';
import { Menu, MenuItem, MenuItemLabel, MenuSeparator } from '@/components/gluestack/menu';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { MoreHorizontalIcon } from '@hugeicons-pro/core-solid-rounded';
import {
  MessageDone01Icon,
  MessageNotification01Icon,
  MessageCancel01Icon,
  UserCircle02Icon,
  Alert02Icon,
  RestoreBinIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { MenuItemIcon } from '@/components/ui/MenuItemIcon';
import { Message } from '@/models/Message';
import { useTranslation } from 'react-i18next';
import { useSupportService } from '@/hooks/useSupportService';
import { ReportReasonSheet } from '@/components/domain/common/ReportReasonSheet';
import type { MessageListType } from '~/constants/lists';
import { MessagesService } from '~/services/domain/MessagesService';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useQueryClient } from '@tanstack/react-query';
import { useQueryCacheUtils } from '@/hooks/useQueryCacheUtils';
import { useRouter } from 'expo-router';
import { useCommunity } from '@/hooks/useCommunity';

interface IMessageContextMenuProps {
  message: Message;
  onMessageDeleted?: (messageId: number) => void;
  listType?: MessageListType;
}

export function MessageContextMenu({
  message,
  onMessageDeleted,
  listType,
}: IMessageContextMenuProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { submitPolicyViolationForConversation } = useSupportService();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { removeCacheItem, updateCacheItem } = useQueryCacheUtils();
  const router = useRouter();
  const community = useCommunity();

  // Same rule as the conversation header: the community's own account is never reported
  const isAdminConversation =
    !!community?.name && message.Member?.UserName?.toLowerCase() === community.name.toLowerCase();

  const handleMarkRead = async () => {
    try {
      await MessagesService.markAsRead(message.Id);
      updateCacheItem('messages', message.Id, { IsRead: true }, 'MessageItems');
    } catch (error) {
      showErrorToast(t('messages.markAsRead.error'), error);
    }
  };

  const handleMarkUnread = async () => {
    try {
      await MessagesService.markAsUnread(message.Id);
      updateCacheItem('messages', message.Id, { IsRead: false }, 'MessageItems');
    } catch (error) {
      showErrorToast(t('messages.markAsUnread.error'), error);
    }
  };

  const [showReportReasonSheet, setShowReportReasonSheet] = useState(false);

  const handleReport = () => {
    setShowReportReasonSheet(true);
  };

  const handleUndoDelete = async () => {
    try {
      await MessagesService.undeleteMessage(message.Id);
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      showSuccessToast(t('messages.undelete.success'));
    } catch (error) {
      showErrorToast(t('messages.undelete.error'), error);
    }
  };

  const handleRestore = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await MessagesService.undeleteMessage(message.Id);
      removeCacheItem('messages', message.Id, 'MessageItems');
      onMessageDeleted?.(message.Id);
      // The other folders are not mounted here and lists never refetch on mount, so drop their
      // cached pages: the restored conversation shows up when they are next opened.
      queryClient.removeQueries({
        queryKey: ['messages'],
        predicate: (query) => query.queryKey[1] !== 'deleted',
      });
      showSuccessToast(t('messages.undelete.success'));
    } catch (error) {
      showErrorToast(t('messages.undelete.error'), error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await MessagesService.deleteMessage(message.Id);
      removeCacheItem('messages', message.Id, 'MessageItems');
      onMessageDeleted?.(message.Id);
      showSuccessToast(
        t('messages.delete.success'),
        {
          text: t('common.undo'),
          onPress: handleUndoDelete,
        },
        'top'
      );
    } catch (error) {
      showErrorToast(t('messages.delete.error'), error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Menu
        placement="bottom right"
        trigger={(props: any) => (
          <Pressable {...props} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
            <HugeIconsWrapper icon={MoreHorizontalIcon} size={20} className="text-typography-700" />
          </Pressable>
        )}>
        {message.IsRead ? (
          <MenuItem
            key="markUnread"
            textValue={t('messages.menu.markUnread')}
            onPress={handleMarkUnread}>
            <MenuItemIcon icon={MessageNotification01Icon} />
            <MenuItemLabel>{t('messages.menu.markUnread')}</MenuItemLabel>
          </MenuItem>
        ) : (
          <MenuItem key="markRead" textValue={t('messages.menu.markRead')} onPress={handleMarkRead}>
            <MenuItemIcon icon={MessageDone01Icon} />
            <MenuItemLabel>{t('messages.menu.markRead')}</MenuItemLabel>
          </MenuItem>
        )}

        <MenuItem
          key="viewProfile"
          textValue={t('messages.menu.viewProfile')}
          onPress={() => router.push(`/@/${message.Member.UserName}`)}>
          <MenuItemIcon icon={UserCircle02Icon} />
          <MenuItemLabel>{t('messages.menu.viewProfile')}</MenuItemLabel>
        </MenuItem>

        <MenuSeparator />
        {listType === 'deleted' ? (
          <MenuItem
            key="restore"
            textValue={t('messages.menu.restoreConversation')}
            onPress={handleRestore}
            disabled={isLoading}>
            <MenuItemIcon icon={RestoreBinIcon} />
            <MenuItemLabel>{t('messages.menu.restoreConversation')}</MenuItemLabel>
          </MenuItem>
        ) : (
          <MenuItem
            key="delete"
            textValue={t('messages.menu.deleteConversation')}
            onPress={handleDelete}
            disabled={isLoading}>
            <MenuItemIcon icon={MessageCancel01Icon} className="text-error-500" />
            <MenuItemLabel>{t('messages.menu.deleteConversation')}</MenuItemLabel>
          </MenuItem>
        )}

        {!isAdminConversation && (
          <MenuItem key="report" textValue={t('messages.menu.reportConversation')} onPress={handleReport}>
            <MenuItemIcon icon={Alert02Icon} className="text-error-500" />
            <MenuItemLabel>{t('messages.menu.reportConversation')}</MenuItemLabel>
          </MenuItem>
        )}
      </Menu>
      <ReportReasonSheet
        isOpen={showReportReasonSheet}
        onClose={() => setShowReportReasonSheet(false)}
        subject="conversation"
        onReasonSelected={(reason) => submitPolicyViolationForConversation(message.Id, reason)}
      />
    </>
  );
}
